import React from 'react';
import api from '../services/api';

// Fill City and State from a ZIP.
//
// A ZIP is the part of an address people reliably know, and it determines city and
// state outright — so typing five digits should not then require typing the two
// fields it implies.
//
// Only fills fields this hook filled before, or empty ones. Typing a city by hand
// and then entering a ZIP must not overwrite what you typed, and correcting a ZIP
// after an autofill SHOULD update the pair it previously set — those two rules are
// why it tracks its own writes rather than just checking for blanks.
//
// Results are cached per ZIP for the life of the form: the same few ZIPs come up
// repeatedly, and the lookup is rate-limited upstream.
export default function useZipLookup(onResolved) {
  const filledRef = React.useRef({ city: null, state: null });
  const cacheRef = React.useRef(new Map());
  const seqRef = React.useRef(0);

  const lookup = React.useCallback((zip, currentCity, currentState) => {
    const digits = String(zip || '').replace(/\D/g, '');
    if (digits.length < 5) return;
    const key = digits.slice(0, 5);

    const apply = (data) => {
      if (!data || !data.found) return;
      const cityIsOurs = !currentCity || currentCity === filledRef.current.city;
      const stateIsOurs = !currentState || currentState === filledRef.current.state;

      const next = {};
      if (data.city && cityIsOurs) { next.city = data.city; filledRef.current.city = data.city; }
      if (data.state && stateIsOurs) { next.state = data.state; filledRef.current.state = data.state; }
      if (Object.keys(next).length) onResolved(next);
    };

    if (cacheRef.current.has(key)) { apply(cacheRef.current.get(key)); return; }

    const mine = ++seqRef.current;
    api.get(`/address/zip/${key}`)
      .then((res) => {
        // A slower earlier lookup must not overwrite a newer ZIP's answer.
        if (mine !== seqRef.current) return;
        cacheRef.current.set(key, res.data);
        apply(res.data);
      })
      .catch(() => { /* typing the city by hand still works */ });
  }, [onResolved]);

  // Register a city/state that something ELSE wrote into the form, so this hook
  // treats them as replaceable rather than as hand-typed.
  //
  // Without it, any prefill blocks the lookup permanently. Adding a property
  // manager fills the job site with their office address, city included; changing
  // that address to the actual job site and typing its ZIP then did nothing,
  // because the hook saw a city it had not written and assumed a human had. The
  // protection is still exactly as strong for anything actually typed — the form
  // just now says which values were its own doing.
  lookup.claim = (city, state) => {
    filledRef.current = {
      city: city ? String(city) : null,
      state: state ? String(state) : null,
    };
  };

  return lookup;
}
